import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useRouteMatch, withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import compose from 'lodash.flowright';
import { useLocation } from 'react-router';
import groupBy from 'lodash.groupby';
import qs from 'query-string';
import { useApolloClient } from 'react-apollo';

import { getCheckoutCartTotalItems } from '../../../../services/checkout/redux/checkoutSelectors';
import useEventCollages from '../../../pro/views/Shop/components/EventCollages/hooks/useEventCollages';
import EventCollageItem from '../../../pro/views/Shop/components/EventCollages/components/EventCollageItem';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import CheckoutCartModal, {
  CHECKOUT_CART_MODAL,
} from '../../../../services/checkout/components/CheckoutCart/modal';
import { toggleAlbumCustomPrintCartItem } from '../../../../services/checkout/redux/checkoutActions';
import { DESIGN_MODES } from '../../../../components/global/CardDesigner/constants';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import { CollageDesignerSuccessModal } from '../../../../components/global/CollageDesigner/components/CollageDesignerSuccess';
import EventCollageSuccessModal from '../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/EventCollagesOption/components/EventCollageSuccessModal';
import Header from '../../../../components/global/Header';
import useOpenModal from '../../../../hooks/useOpenModal';
import { getAsset } from '../../../../util/assets';
import Button from '../../../../components/global/Button';
import ShoppingBasketIcon from '../../../../components/icons/ShoppingBasketIcon';
import NotificationCounter from '../../../pro/components/NotificationCounter';
import useCloseModal from '../../../../hooks/useCloseModal';
import { CHECKOUT_CART_SUCCESS_MODAL } from '../../../../services/checkout/components/CheckoutCartSuccess/modal';
import { CheckoutCartSuccessModal } from '../../../../services/checkout/components/CheckoutCartSuccess';
import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import Logo from '../../../../components/global/Logo';
import getConfig from '../../../../util/config';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
};

const EventCollages = ({ onAlbumCustomPrintToggle }) => {
  const client = useApolloClient();
  const match = useRouteMatch();
  const location = useLocation();
  const { albumFolderId } = match?.params ?? {};
  const { identity_ids: identityIds = [] } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const { loading, eventCollages } = useEventCollages({ unfiltered: true });
  const totalCartItems = useSelector(state => getCheckoutCartTotalItems(state));
  const config = getConfig();

  const [collageModalOpen, setCollageModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  const [selectedEventCollage, setSelectedEventCollage] = useState(null);
  const [completedAccountCard, setCompletedAccountCard] = useState(null);

  const albumFolderCollages = useMemo(() => {
    if (!albumFolderId || !eventCollages?.length) {
      return [];
    }

    return eventCollages.filter(
      ({ album_custom_print: albumCustomPrint }) =>
        albumCustomPrint?.album?.root_album_folder?.id === albumFolderId,
    );
  }, [eventCollages]);

  const albumFolderCollageGroups = useMemo(() => {
    if (albumFolderCollages.length === 0) {
      return [];
    }

    if (identityIds?.length > 0) {
      return groupBy(
        albumFolderCollages.filter(({ selected_identity: selIdentity }) =>
          identityIds.includes(selIdentity.identity.id),
        ),
        ({ selected_identity: selIdentity }) => selIdentity.identity.id,
      );
    }

    return groupBy(
      albumFolderCollages,
      ({ selected_identity: selIdentity }) => selIdentity.identity.id,
    );
  }, [albumFolderCollages]);

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Event Collages &ndash; Waldo</title>
      </Helmet>

      <div className="public-account-card-wrap event-collages-configurator">
        <div className="event-collages-configurator-background" />
        <Header className="public-account-card-header">
          <Logo linksTo={config.marcomUrl} externalLink />
          <h1 className="public-account-card-header-title">
            Send a collage to your campers!
          </h1>
          {totalCartItems > 0 && (
            <Button
              btnStyle="reset"
              className="public-account-card-header-cart"
              onClick={() => openModal(CHECKOUT_CART_MODAL)}
            >
              <NotificationCounter
                count={totalCartItems ?? 0}
                Icon={ShoppingBasketIcon}
                notificationIconColor="#0d84fe"
              />
            </Button>
          )}
        </Header>
        {!loading && !albumFolderCollageGroups && (
          <div className="text-center">
            <img
              width="44px"
              className="mb-2 grayscale-faded"
              src={getAsset('/waldo-icon-error.svg')}
              alt="Waldo Icon"
            />
            <h4>Oops!</h4>
            <p>
              It looks like there are no collages available for the event
              you&apos;ve selected.
            </p>
          </div>
        )}
        {!loading && Object.keys(albumFolderCollageGroups ?? {}).length > 0 && (
          <div className="event-collages-container px-2 px-mb-0">
            <Fragment>
              {Object.keys(albumFolderCollageGroups).map(identityId => {
                const identityCollages = albumFolderCollageGroups[identityId];
                const { identity } = identityCollages[0].selected_identity;

                return (
                  <div key={identity.id}>
                    <h4 className="mx-2 mx-mb-0 mt-2">
                      Send a collage to {identity.name}{' '}
                      <IdentityAvatarContainer
                        key={identity.id}
                        identity={identity}
                        title={identity.name}
                        className="mr-1"
                        selfieType="any"
                      />
                      !
                    </h4>
                    {identityCollages.map(eventCollage => (
                      <EventCollageItem
                        key={eventCollage.id}
                        eventCollage={eventCollage}
                        onConfigure={() => {
                          setSelectedEventCollage(eventCollage);
                          setCollageModalOpen(true);
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </Fragment>

            <AccountCardAlbumCustomPrintConfiguratorModal
              forceOpen={collageModalOpen}
              albumCustomPrint={selectedEventCollage?.album_custom_print}
              onClose={() => {
                setCollageModalOpen(false);
              }}
              onFinished={({ accountCard }) => {
                setCompletedAccountCard(accountCard);
                setCollageModalOpen(false);
                setSuccessModalOpen(true);
              }}
              defaultAccountCard={completedAccountCard}
              deleteAccountCardOnDismiss
              identity={selectedEventCollage?.selected_identity.identity}
              albumMembershipGroup={{
                is_album: true,
                id: selectedEventCollage?.album_custom_print.album?.id,
                album: selectedEventCollage?.album_custom_print.album,
              }}
              isAllPhotos
              buttonText="Create my Camper Collage"
              inlineButton
              title="Design your collage!"
              altClassName="event-collage-option-account-card-configurator-modal"
            />
            <CollageDesignerSuccessModal
              forceOpen={successModalOpen}
              accountCard={completedAccountCard}
              title="Looks great!"
              onAddToCart={() => {
                onAlbumCustomPrintToggle({
                  albumCustomPrint: selectedEventCollage?.album_custom_print,
                  albumMembershipGroup: {
                    is_album: true,
                    id: selectedEventCollage?.album_custom_print.album?.id,
                    album: selectedEventCollage?.album_custom_print.album,
                  },
                  identity: selectedEventCollage?.selected_identity.identity,
                  shipping: true,
                  isAllPhotos: true,
                  designMode: DESIGN_MODES.COLLAGES,
                  configuration: { accountCard: completedAccountCard },
                });
                setSuccessModalOpen(false);
                setCheckoutModalOpen(true);
              }}
              onGoBack={() => {
                setSuccessModalOpen(false);
                setCollageModalOpen(true);
              }}
              onClose={() => {
                setSuccessModalOpen(false);
              }}
            />
            {checkoutModalOpen && (
              <EventCollageSuccessModal
                forceOpen
                eventCollage={selectedEventCollage?.album_custom_print}
                onCheckout={() => {
                  setSelectedEventCollage(null);
                  setCompletedAccountCard(null);
                  setCheckoutModalOpen(false);
                  openModal(CHECKOUT_CART_MODAL);
                }}
                onDismiss={() => {
                  setCheckoutModalOpen(false);
                }}
              />
            )}
          </div>
        )}
        <CheckoutCartModal
          onSuccess={() => {
            // We gotta nuke everything until we can find a way to clear
            // only the relevant data that need clearing (i.e. AlbumMembershipGroup
            // and purchases, purchased custom prints, etc).
            client.resetStore();
            closeModal(CHECKOUT_CART_MODAL);
            openModal(CHECKOUT_CART_SUCCESS_MODAL);
          }}
        />
        <CheckoutCartSuccessModal
          onButtonClick={() => closeModal(CHECKOUT_CART_SUCCESS_MODAL)}
        />
      </div>
    </Fragment>
  );
};

EventCollages.propTypes = propTypes;

const mapDispatchToProps = {
  onAlbumCustomPrintToggle: toggleAlbumCustomPrintCartItem,
};

export default compose(
  withRouter,
  connect(null, mapDispatchToProps),
)(EventCollages);
